/**
 * Date display for `yearMonth` values ("YYYY-MM", null for "present"), shared
 * by the Inspector, in-world panels and the content build (tile subtitles, the
 * static shell) so dates read the same everywhere. No imports: the build loads
 * it natively.
 */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2025-09" → "Sep 2025". Null (an open end) shows `nullLabel`, e.g. "Present". */
export function formatYearMonth(value: string | null, nullLabel = "Present"): string {
  if (value === null) return nullLabel;

  const [year, month] = value.split("-");
  const name = MONTHS[Number(month) - 1];
  return name && year ? `${name} ${year}` : value;
}

/** "Sep 2025 - Present". Date ranges use a spaced hyphen everywhere (owner's style). */
export function formatDateRange(start: string, end: string | null, nullLabel = "Present"): string {
  return `${formatYearMonth(start)} - ${formatYearMonth(end, nullLabel)}`;
}

/** A span of `yearMonth` dates; a null end is ongoing. */
interface Dated {
  readonly start: string;
  readonly end: string | null;
}

function yearOf(yearMonth: string): number {
  return Number(yearMonth.slice(0, 4));
}

/**
 * "2022 - now", "2019 - 2022", or "2018" for a single year.
 * `presentLabel` is what an open-ended range ends with.
 */
export function formatYearRange(spans: readonly Dated[], presentLabel = "now"): string {
  if (spans.length === 0) return "";

  const start = Math.min(...spans.map((span) => yearOf(span.start)));
  if (spans.some((span) => span.end === null)) return `${start} - ${presentLabel}`;

  const end = Math.max(...spans.map((span) => yearOf(span.end ?? span.start)));
  return start === end ? `${start}` : `${start} - ${end}`;
}
